import { HomeHeader } from '../components/home/Header';
import { HomeFooter } from '../components/home/Footer';
import { config } from '../lib/config';
import './HomePage.css';

interface Props {
  loggedInTo?: string | null;
}

interface Package {
  name: string;
  audience: string;
  municipalities: string;
  users: string;
  profiles: string;
  dashboard: string;
  price: string;
}

interface Client {
  name: string;
  logo: string;
  scale?: number;
}

const CLIENTS: Client[] = [
  { name: 'TPN West', logo: '/clients/tpn-west.svg' },
  { name: 'KHN', logo: '/clients/khn.png', scale: 1.5 },
  { name: 'Natuurmonumenten', logo: '/clients/natuurmonumenten.jpg', scale: 0.9 },
  { name: 'Huis voor de Binnenstad', logo: '/clients/huis-voor-de-binnenstad.png', scale: 1.5 },
  { name: 'Talis', logo: '/clients/talis.svg', scale: 1.4 },
];

const PACKAGES: Package[] = [
  {
    name: 'XS',
    audience: 'ZZP, individueel MKB',
    municipalities: '1',
    users: '1',
    profiles: '1',
    dashboard: 'Nee',
    price: '€79',
  },
  {
    name: 'S',
    audience: 'Klein MKB, bedrijvenvereniging',
    municipalities: '1 tot 3',
    users: '2',
    profiles: '1',
    dashboard: 'Nee',
    price: '€149',
  },
  {
    name: 'M',
    audience: 'Regionale brancheorganisatie, corporatie',
    municipalities: '4 tot 10',
    users: '5',
    profiles: '3',
    dashboard: 'Ja',
    price: '€399',
  },
  {
    name: 'L',
    audience: 'Provinciale branchevereniging',
    municipalities: '11 tot 25',
    users: '10',
    profiles: '5',
    dashboard: 'Ja',
    price: '€699',
  },
  {
    name: 'XL',
    audience: 'Bovenregionaal, meerdere provincies',
    municipalities: '26 tot 75',
    users: '20',
    profiles: '10',
    dashboard: 'Ja',
    price: '€1.299',
  },
  {
    name: 'XXL',
    audience: 'Landelijk, sector-dekkend',
    municipalities: '76 tot 150',
    users: 'onbeperkt',
    profiles: 'onbeperkt',
    dashboard: 'Ja, plus API',
    price: '€2.499',
  },
];

export function HomePage({ loggedInTo }: Props) {
  const ctaLabel = loggedInTo ? 'Naar dashboard' : 'Inloggen';
  const ctaTo = loggedInTo ?? '/login';

  const bookingProps = { href: config.bookingUrl, target: '_blank', rel: 'noreferrer' };

  return (
    <div className="home">
      <a href="#hoofdinhoud" className="skip-link">
        Naar de inhoud
      </a>
      <HomeHeader ctaLabel={ctaLabel} ctaTo={ctaTo} />

      <main id="hoofdinhoud">
        {/* Hero */}
        <section className="home-hero">
          <div className="home-hero-grid">
            <div className="home-hero-text">
              <h1>Op tijd weten welk beleid eraan komt.</h1>
              <p className="lead">
                Elke dag lezen wij de stukken van gemeenteraden. Raakt iets je organisatie? Dan krijg je een korte mail.
                Geen ruis, geen alarm. Alleen wat telt.
              </p>
              <div className="cta-row">
                <a {...bookingProps} className="btn">
                  Plan een kennismaking
                </a>
                <a href="#hoe" className="btn outline">
                  Hoe het werkt
                </a>
              </div>
              <ul className="home-hero-trust">
                <li>Wij volgen 300+ gemeenten</li>
                <li>Eén mail per dag</li>
                <li>Geen ruis, alleen signaal</li>
              </ul>
            </div>
            <HeroMockup />
          </div>
        </section>

        {/* Wat is Beleidssignaal */}
        <section id="wat" className="home-section alt">
          <div className="home-container">
            <span className="eyebrow">Wat is Beleidssignaal</span>
            <h2>Een baken, geen sirene.</h2>
            <p className="section-lead">
              Elke dag lezen we de stukken uit je gemeenten. Onze AI vergelijkt elk stuk met je profiel: de thema's,
              woorden en lopende dossiers van je organisatie. Past het? Dan krijg je een seintje.
            </p>
            <div className="home-features">
              <FeatureBlock
                icon="scan"
                title="Scannen"
                body="We lezen elke dag alle stukken van de gemeenteraad: voorstellen, moties, vragen en besluiten."
              />
              <FeatureBlock
                icon="analyse"
                title="Analyseren"
                body="Per stuk vertellen we: hoort dit bij je organisatie, hoe dringend is het, en wat zou je kunnen doen. In twee zinnen."
              />
              <FeatureBlock
                icon="send"
                title="Signaleren"
                body="Eén mail per dag met wat nieuw is. Niets nieuws? Dan ook geen mail. Vanuit elke mail klik je door naar het dashboard met alle historie en filters."
              />
            </div>
          </div>
        </section>

        {/* Voor wie */}
        <section id="voor-wie" className="home-section">
          <div className="home-container">
            <span className="eyebrow">Voor wie</span>
            <h2>Organisaties die niet de tijd hebben om elke gemeenteraad te volgen.</h2>
            <p className="section-lead">
              Brancheverenigingen, woningcorporaties, natuurorganisaties, ondernemerscollectieven en horeca-koepels
              gebruiken Beleidssignaal om hun leden te informeren en op tijd te kunnen reageren.
            </p>
            <div className="home-audience">
              <AudienceItem icon="branche" title="Brancheverenigingen">
                Lokaal én regionaal. Zie in één oogopslag wat in elke gemeente speelt.
              </AudienceItem>
              <AudienceItem icon="industrie" title="Bedrijvenverenigingen">
                Volg parkmanagement, milieu, infrastructuur en vergunningen voor je terrein.
              </AudienceItem>
              <AudienceItem icon="huis" title="Woningcorporaties">
                Prestatieafspraken, woonbeleid, bouwregels en parkeernormen, direct als ze in de raad komen.
              </AudienceItem>
              <AudienceItem icon="natuur" title="Natuur- en milieuorganisaties">
                Vergunningen, omgevingsvisies, beheerbesluiten in de gebieden die je beheert.
              </AudienceItem>
              <AudienceItem icon="horeca" title="Horeca, retail en cultuur">
                Terrasregels, openingstijden, evenementen, vergunningen. Alles wat je zaak raakt.
              </AudienceItem>
            </div>
          </div>
        </section>

        {/* Klanten - TIJDELIJK UIT */}
        {false && (
          <section id="klanten" className="home-section">
            <div className="home-container">
              <span className="eyebrow">Klanten</span>
              <h2>Organisaties die op Beleidssignaal rekenen.</h2>
              <div className="home-clients">
                {CLIENTS.map((client) => (
                  <div
                    key={client.name}
                    className="home-clients-logo"
                    style={{ ['--logo-scale' as string]: client.scale ?? 1 }}
                  >
                    <img src={client.logo} alt={client.name} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Hoe het werkt */}
        <section id="hoe" className="home-section alt">
          <div className="home-container">
            <span className="eyebrow">Hoe het werkt</span>
            <h2>Vier stappen, daarna draait het op de achtergrond.</h2>
            <div className="home-steps">
              <div className="home-step">
                <span className="home-step-num">01</span>
                <h3>Samen het profiel opstellen</h3>
                <p>
                  We schrijven samen op wat je organisatie doet, welke thema's spelen, en welke woorden wel en niet
                  relevant zijn. Dit is de basis voor onze AI.
                </p>
              </div>
              <div className="home-step">
                <span className="home-step-num">02</span>
                <h3>Continue scan</h3>
                <p>
                  We houden de gemeenten die je volgt dag en nacht in de gaten, ook in het weekend. Zodra er een nieuw
                  stuk binnenkomt, pakken we het op.
                </p>
              </div>
              <div className="home-step">
                <span className="home-step-num">03</span>
                <h3>AI-analyse</h3>
                <p>
                  Per nieuw stuk kijken we: hoort dit bij je organisatie? Hoe dringend is het? En in twee zinnen: waar
                  gaat het over en wat kun je doen.
                </p>
              </div>
              <div className="home-step">
                <span className="home-step-num">04</span>
                <h3>Signaal in je inbox</h3>
                <p>
                  Eén mail per dag met alleen relevante signalen. Geen mail als er niets nieuws is. Vanuit de mail klik
                  je door naar het dashboard met alle historie en filters.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tarieven - TIJDELIJK UIT */}
        {false && (
          <section id="tarieven" className="home-section">
            <div className="home-container">
              <span className="eyebrow">Tarieven</span>
              <h2>Eén tarief per maand, jaarcontract als standaard.</h2>
              <p className="section-lead">
                Pakket op basis van het aantal gemeenten dat je wilt monitoren, het aantal gebruikers en het aantal
                profielen. Schalen kan op elk moment.
              </p>
              <table className="home-pricing-table">
                <thead>
                  <tr>
                    <th>Pakket</th>
                    <th>Doelgroep</th>
                    <th>Gemeenten</th>
                    <th>Gebruikers</th>
                    <th>Profielen</th>
                    <th>Dashboard</th>
                    <th>Per maand</th>
                  </tr>
                </thead>
                <tbody>
                  {PACKAGES.map((plan) => (
                    <tr key={plan.name}>
                      <td className="pkg-name">{plan.name}</td>
                      <td>{plan.audience}</td>
                      <td>{plan.municipalities}</td>
                      <td>{plan.users}</td>
                      <td>{plan.profiles}</td>
                      <td>{plan.dashboard}</td>
                      <td className="pkg-price">{plan.price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="home-pricing-disclaimer">
                Bedragen exclusief btw, op maandbasis. Jaarcontract als standaard.
              </p>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section id="faq" className="home-section narrow">
          <div className="home-container">
            <span className="eyebrow">Veelgestelde vragen</span>
            <h2>Kort en concreet.</h2>
            <div className="home-faq-list">
              <details>
                <summary>Hoe vaak krijg ik een mail?</summary>
                <p>
                  Eén mail per dag, rond 09:00, met de signalen van de afgelopen 24 uur. Is er niets gevonden? Dan krijg
                  je ook geen mail.
                </p>
              </details>
              <details>
                <summary>Welke gemeenten kunnen jullie monitoren?</summary>
                <p>
                  Ruim 300 gemeenten publiceren hun raadsstukken openbaar. Die volgen we allemaal. Doet een gemeente uit
                  je scope dat (nog) niet? Dan kijken we samen of het toch lukt.
                </p>
              </details>
              <details>
                <summary>Wat doet de AI precies?</summary>
                <p>
                  Voor elk nieuw document beoordelen we of het raakt aan je profiel, op welk niveau (laag, normaal,
                  hoog). We schrijven een samenvatting in twee zinnen en geven een aanbevolen actie. Wij controleren
                  steekproefsgewijs en je kunt zelf feedback geven per signaal. Daar leren we van.
                </p>
              </details>
              <details>
                <summary>Wat als een signaal niet relevant is?</summary>
                <p>
                  In het dashboard kun je per signaal aangeven dat het niet relevant is voor je organisatie. Wij
                  gebruiken dat om je profiel beter te maken.
                </p>
              </details>
              <details>
                <summary>Hoe werkt het dashboard?</summary>
                <p>
                  Iedereen krijgt een dashboard. Vanuit elke mail klik je direct door. Daar zie je de geschiedenis van
                  alle signalen, kun je filteren op urgentie, periode of gemeente, en geef je feedback per signaal.
                </p>
              </details>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="home-section alt narrow home-contact">
          <div className="home-container">
            <span className="eyebrow">Contact</span>
            <h2>Klaar voor je eerste signaal?</h2>
            <p>
              Plan een kennismaking van een half uur. We bekijken samen welke gemeenten en thema's voor je relevant zijn
              en wat een passend pakket is.
            </p>
            <a {...bookingProps} className="btn">
              Plan een kennismaking
            </a>
          </div>
        </section>
      </main>

      <HomeFooter />
    </div>
  );
}

interface MockSignal {
  priority: 'high' | 'normal' | 'low';
  title: string;
  snippet: string;
  when: string;
}

const MOCK_SIGNALS: MockSignal[] = [
  {
    priority: 'high',
    title: 'Voorstel wijziging parkeerbeleid binnenstad',
    snippet:
      'Raadsvoorstel om parkeertarieven en venstertijden aan te passen. Komt deze maand in een raadsvergadering.',
    when: '08:42',
  },
  {
    priority: 'normal',
    title: 'Wijziging Algemene Plaatselijke Verordening',
    snippet:
      'College stelt aanpassingen voor terras- en exploitatievergunningen voor. Relevant voor horeca-exploitanten.',
    when: '08:42',
  },
  {
    priority: 'high',
    title: 'Omgevingsprogramma luchtkwaliteit',
    snippet:
      'Nieuw kader bepaalt emissienormen voor bedrijven in zwaardere milieucategorieën. Inspraakperiode start binnenkort.',
    when: '07:15',
  },
  {
    priority: 'low',
    title: 'Centrumvisie in consultatiefase',
    snippet: 'Lange-termijnvisie op binnenstadsontwikkeling. Relevant als kader, geen acute actie.',
    when: '07:15',
  },
];

function HeroMockup() {
  const today = new Date().toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
  });
  return (
    <div className="hero-mockup" aria-hidden="true">
      <div className="hero-mockup-header">
        <span className="hero-mockup-title">Beleidssignaal · Dagoverzicht</span>
        <span className="hero-mockup-date">{today}</span>
      </div>
      <div className="hero-mockup-list">
        {MOCK_SIGNALS.map((signal, i) => (
          <div key={i} className="hero-mockup-signal" data-priority={signal.priority} style={{ ['--i' as string]: i }}>
            <div className="hero-mockup-signal-meta">
              <span>{signal.priority === 'high' ? 'Hoog' : signal.priority === 'normal' ? 'Normaal' : 'Laag'}</span>
              <span className="when">{signal.when}</span>
            </div>
            <p className="hero-mockup-signal-title">{signal.title}</p>
            <p className="hero-mockup-signal-snippet">{signal.snippet}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

type FeatureIcon = 'scan' | 'analyse' | 'send';

const ICONS: Record<FeatureIcon, JSX.Element> = {
  scan: (
    <svg
      className="icon"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      stroke="#1B2433"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="13" cy="13" r="8" />
      <line x1="19" y1="19" x2="26" y2="26" />
      <circle cx="13" cy="13" r="2" fill="#D97706" stroke="none" />
    </svg>
  ),
  analyse: (
    <svg
      className="icon"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      stroke="#1B2433"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="6" y="5" width="20" height="22" rx="1.5" />
      <line x1="10" y1="11" x2="22" y2="11" />
      <line x1="10" y1="16" x2="22" y2="16" stroke="#D97706" strokeWidth="3" strokeLinecap="round" />
      <line x1="10" y1="20" x2="18" y2="20" />
      <line x1="10" y1="23" x2="20" y2="23" />
    </svg>
  ),
  send: (
    <svg
      className="icon"
      viewBox="0 0 32 32"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      stroke="#1B2433"
      strokeWidth="1.5"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="4" y="9" width="22" height="15" rx="1.5" />
      <path d="M4 10 L15 18 L26 10" />
      <circle cx="26" cy="9" r="4" fill="#D97706" stroke="#FAFAF7" strokeWidth="1.5" />
    </svg>
  ),
};

function FeatureBlock({ icon, title, body }: { icon: FeatureIcon; title: string; body: string }) {
  return (
    <div className="home-feature">
      {ICONS[icon]}
      <h3>{title}</h3>
      <p>{body}</p>
    </div>
  );
}

type AudienceIcon = 'branche' | 'industrie' | 'huis' | 'natuur' | 'horeca';

const AUDIENCE_ICONS: Record<AudienceIcon, JSX.Element> = {
  branche: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="3" x2="12" y2="21" />
      <path d="M3 12 Q12 6 21 12" />
      <path d="M3 12 Q12 18 21 12" />
    </svg>
  ),
  industrie: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 21 V11 L9 14 V11 L15 14 V11 L21 14 V21 Z" />
      <line x1="3" y1="21" x2="21" y2="21" />
      <rect x="6" y="3" width="3" height="9" />
      <line x1="6" y1="6" x2="9" y2="6" />
      <rect x="9.5" y="17" width="2" height="4" />
      <rect x="14.5" y="17" width="2" height="4" />
    </svg>
  ),
  huis: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 11 L12 4 L21 11" />
      <path d="M5 10 V20 H19 V10" />
      <rect x="10" y="14" width="4" height="6" />
    </svg>
  ),
  natuur: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2 C8 8 8 12 12 14 C16 12 16 8 12 2 Z" />
      <line x1="12" y1="14" x2="12" y2="22" />
      <path d="M5 18 Q12 16 19 18" />
    </svg>
  ),
  horeca: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 3 V11 A6 6 0 0 0 18 11 V3" />
      <line x1="6" y1="7" x2="18" y2="7" />
      <line x1="12" y1="17" x2="12" y2="21" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </svg>
  ),
};

function AudienceItem({ icon, title, children }: { icon: AudienceIcon; title: string; children: React.ReactNode }) {
  return (
    <div className="home-audience-item">
      <span className="home-audience-icon">{AUDIENCE_ICONS[icon]}</span>
      <strong>{title}</strong>
      <span>{children}</span>
    </div>
  );
}
